// Stateful manager for the abandoned-cart feature. Owns the trigger
// listeners (visibility-change, exit-intent, donate-modal-close),
// holds the engagement state + the abandoned-cart options the donate
// widget broadcasts, fires the dialog at most once per page lifetime
// via a caller-supplied `onFire(opts, reason)` callback. `onFire` may
// return a closer fn (or nothing); when it does, the manager calls it
// on donation completion to dismiss an already-open dialog.
//
// One instance per "host context":
//
//   - Mode 1 — donate widget at top level (standalone): instance
//     lives in the donate-widget pack; listens to its own document.
//   - Modes 2 + 3 — donate widget embedded as iframe or modal:
//     instance lives in the embed script (widget.js); listens to
//     the partner page's document.
//
// Signals flow in via setEngagement / onDonationCompleted /
// onDonateModalClosed. The first two come from the donate widget
// (direct call in mode 1, over the channel in modes 2+3). The third
// fires when donationModal.js's <dialog> close event fires (mode 3
// only — no donate-modal exists in modes 1+2).
//
// Suppression logic:
//
//   - Once fired, every trigger is a no-op until createAbandonedCart
//     is called again (page reload or hot restart).
//   - Donation completion sets fired = true so a post-donation exit
//     can't re-pop.
//   - Exit-intent is suppressed while a donate modal is open, since
//     mouse-toward-chrome there is more likely the donor reaching for
//     the modal's close button than tab-close intent. The modal's
//     own close event becomes the trigger.
//
// URL building: the donate widget broadcasts the abandoned-cart URL
// in parts (origin, partnerKey, defaultExternalId, locale) rather
// than as a pre-built string, and separately announces donor
// form-state (email, picked partner_item) as the donor fills the
// form. The manager assembles the URL at trigger time, swapping the
// path's external_id for the donor's picked one when present and
// appending email as a query param. "No event = default" means a
// donor who never touches the multi-org select gets a URL pointing
// at the goal partner_item (the default the donate widget was
// rendered with) — same partner_item the donor was already looking
// at.
//
// The URL is absolute because the dialog iframe mounts on the
// partner page in modes 2+3; a relative path there would resolve
// against the partner's origin and 404. `opts.origin` is the donate
// widget's own location.origin (pledge.to in all modes).
//
// See project_abandoned_cart memory for the broader plan.

const ABANDONED_CART_PATH = '/widgets/abandoned_cart'

const buildSrc = (opts, formState) => {
  const externalId = formState.partnerItemExternalId || opts.defaultExternalId
  const qp = new URLSearchParams({ locale: opts.locale })
  if (formState.email) qp.set('email', formState.email)
  if (opts.styleBrandColor) qp.set('style_brand_color', opts.styleBrandColor)
  return `${opts.origin}${ABANDONED_CART_PATH}/${opts.partnerKey}/${externalId}?${qp}`
}

let _instance = null

export const createAbandonedCart = ({ onFire, doc = document } = {}) => {
  // Teardown a previous instance if any — happens on hot restart and
  // in tests that init() the embed script repeatedly.
  if (_instance) _instance.teardown()

  let engagementLevel = 0
  let opts = null
  let formState = {}
  let fired = false
  let donateModalOpen = false
  // Closer returned by onFire for the currently-shown dialog (when the
  // wiring renders one), so we can dismiss it if the donation completes
  // while it's still open.
  let closeFiredDialog = null

  const trigger = reason => {
    if (fired) return
    if (engagementLevel < 1) return
    if (!opts) return
    fired = true
    closeFiredDialog = onFire({
      src: buildSrc(opts, formState),
      ariaLabel: opts.ariaLabel,
      locale: opts.locale,
      background: opts.background,
    }, reason)
  }

  const onVisibilityChange = () => {
    if (doc.hidden) trigger('visibility')
  }

  const onMouseOut = e => {
    if (donateModalOpen) return
    // Mouse leaves toward the browser chrome: relatedTarget null
    // (off-document, not to another element) AND clientY ≤ 0 (top
    // edge — address bar / close button territory). Bottom-edge
    // exits also have relatedTarget=null but clientY > 0; those are
    // typically dock / taskbar moves, not tab-close intent.
    if (e.relatedTarget) return
    if (e.clientY > 0) return
    trigger('exit-intent')
  }

  doc.addEventListener('visibilitychange', onVisibilityChange)
  doc.addEventListener('mouseout', onMouseOut)

  const instance = {
    setEngagement(payload) {
      // Levels are monotonically non-decreasing within a session.
      // Take max so a later mount-time L0 announce (e.g. the donate
      // widget mounting in a modal after MiniDonate already engaged
      // the donor) doesn't regress the manager's state. Reset to L0
      // is via onDonationCompleted, which also flips the fired flag.
      engagementLevel = Math.max(engagementLevel, payload?.level || 0)
      if (payload?.abandonedCart) opts = payload.abandonedCart
    },
    setFormState(patch) {
      // Shallow merge so the iframe can push one field at a time
      // (email separately from partnerItemExternalId) without
      // clobbering the rest.
      formState = { ...formState, ...patch }
    },
    onDonationCompleted() {
      engagementLevel = 0
      fired = true
      // Dismiss the corner dialog if it's still open — the donor just
      // completed a donation, so the "come back and finish" prompt is
      // moot. No-op if nothing fired or it was already dismissed.
      closeFiredDialog?.()
      closeFiredDialog = null
    },
    onDonateModalOpened() { donateModalOpen = true },
    onDonateModalClosed() {
      donateModalOpen = false
      trigger('modal-close')
    },
    teardown() {
      doc.removeEventListener('visibilitychange', onVisibilityChange)
      doc.removeEventListener('mouseout', onMouseOut)
      if (_instance === instance) _instance = null
    },
  }
  _instance = instance
  return instance
}

// Singleton accessor. Donate-modal code paths (donationModal.js) and
// any future cross-module consumers reach for the instance without
// threading it through every caller.
export const getAbandonedCart = () => _instance
